import { useEffect, useRef, useState } from "react";

import { ChevronDown, Download, Settings2, Upload } from "lucide-react";
import * as XLSX from "xlsx";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Separator } from "~/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { toast } from "~/hooks/use-toast";
import { useClientImport } from "~/hooks/useClientImport";
import { useClientSort } from "~/hooks/useClientSort";
import {
  transformOptimizedClient,
  useOptimizedClients,
} from "~/hooks/useOptimizedClients";
import { supabase } from "~/integrations/supabase/client";
import AddClientDialog from "~/pages/clients/components/AddClientDialog";
import { AddressImportDialog } from "~/pages/clients/components/AddressImportDialog";
import AuxiliaryTypeManagementModal from "~/pages/clients/components/AuxiliaryTypeManagementModal";
import ClientSearchBar from "~/pages/clients/components/ClientSearchBar";
import ClientsPagination from "~/pages/clients/components/ClientsPagination";
import ClientsTable from "~/pages/clients/components/ClientsTable";
import SalesChannelManagementModal from "~/pages/clients/components/SalesChannelManagementModal";
import SellerFilter from "~/pages/clients/components/SellerFilter";
import { exportAddressesToExcel } from "~/services/addressExportService";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";

// Shape of client data returned from Supabase
interface DbClient {
  id: string;
  name?: string;
  email?: string;
  phone_number?: string;
  tax_id?: string;
  insured_amount?: number;
  trailing_payment_days?: number;
  risk_checked?: boolean;
  sales_condition_id?: string;
  fiscal_address_id?: string;
  contact_person?: string;
  internal_notes?: string;
  assigned_seller?: string;
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel?: string;
  auxiliary_type?: string;
  sales_channel_name?: string;
  auxiliary_type_name?: string;
  seller_name?: string;
  sales_condition_name?: string;
  deleted_at?: string | null;
  is_active?: boolean;
  deactivation_reason?: string | null;
  profiles?: Record<string, any> | null; // Use Record to avoid 'any' warning
  sales_conditions?: Record<string, any> | null; // Sales condition data from join
  fiscal_address?: Record<string, any> | null; // Fiscal address data from join
  // Use a more specific type to avoid TypeScript errors with Supabase responses
  [key: string]: unknown;
}

// Shape of client data received from AddClientDialog
interface ClientData {
  name: string;
  tax_id: string;
  email: string;
  phone_number: string;
  created_by: string;
  assigned_seller: string;
  sales_condition_id?: string | null;
  id?: string;
}

// Enhanced Client type with assigned_seller field
interface EnhancedClient extends Client {
  assigned_seller: string; // Make assigned_seller required for EnhancedClient
  salesConditionName?: string; // Add sales condition name
  contact_person?: string; // Add contact person
  fiscalStreet?: string; // Add fiscal street
  fiscalComuna?: string; // Add fiscal comuna
  fiscalRegion?: string; // Add fiscal region
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel?: string;
  auxiliary_type?: string;
  sales_channel_name?: string;
  auxiliary_type_name?: string;
}

const mapToClient = (dbClient: DbClient): EnhancedClient => {
  // Extract sales condition name from joined data
  let salesConditionName = "No especificada";
  try {
    if (
      dbClient.sales_conditions &&
      typeof dbClient.sales_conditions === "object"
    ) {
      const salesCondition = dbClient.sales_conditions as { name?: string };
      if (salesCondition.name) {
        salesConditionName = salesCondition.name;
      }
    }
  } catch (e) {
    console.warn("Error parsing sales condition:", e);
  }

  // Extract fiscal address components from joined data
  let fiscalStreet = "";
  let fiscalComuna = "";
  let fiscalRegion = "";

  try {
    if (
      dbClient.fiscal_address &&
      typeof dbClient.fiscal_address === "object"
    ) {
      const address = dbClient.fiscal_address as {
        street?: string;
        number?: string;
        city?: string;
        comuna?: string;
        state?: string;
      };

      // Build street address (street + number)
      const streetParts = [];
      if (address.street) streetParts.push(address.street);
      if (address.number) streetParts.push(address.number);
      fiscalStreet = streetParts.join(" ");

      // Get comuna
      fiscalComuna = address.comuna || "";

      // Format region properly
      if (address.state) {
        const formattedRegion = address.state;

        // Simple and safe region formatting
        if (formattedRegion.toLowerCase().includes("metropolitana")) {
          fiscalRegion = "Región Metropolitana";
        } else {
          // Map known region patterns to proper names
          const regionMappings: Record<string, string> = {
            Tarapacá: "Región de Tarapacá",
            Antofagasta: "Región de Antofagasta",
            Atacama: "Región de Atacama",
            Coquimbo: "Región de Coquimbo",
            Valparaíso: "Región de Valparaíso",
            "O'Higgins": "Región de O'Higgins",
            Maule: "Región de Maule",
            Ñuble: "Región de Ñuble",
            Biobío: "Región de Biobío",
            "La Araucanía": "Región de La Araucanía",
            "Los Ríos": "Región de Los Ríos",
            "Los Lagos": "Región de Los Lagos",
            Aysén: "Región de Aysén",
            Magallanes: "Región de Magallanes",
            "Arica y Parinacota": "Región de Arica y Parinacota",
          };

          // Check if the state matches any known region
          let foundMapping = false;
          for (const [regionName, fullName] of Object.entries(regionMappings)) {
            if (
              formattedRegion.toLowerCase().includes(regionName.toLowerCase())
            ) {
              fiscalRegion = fullName;
              foundMapping = true;
              break;
            }
          }

          // If no mapping found, check for specific problematic patterns
          if (!foundMapping) {
            if (
              formattedRegion.includes("Libertador") &&
              formattedRegion.includes("Higgins")
            ) {
              fiscalRegion = "Región de O'Higgins";
            } else if (!formattedRegion.toLowerCase().startsWith("región")) {
              // If it doesn't start with "Región" and we don't recognize it,
              // just add "Región de" prefix
              fiscalRegion = `Región de ${formattedRegion}`;
            } else {
              fiscalRegion = formattedRegion;
            }
          }
        }
      }
    }
  } catch (e) {
    console.warn("Error parsing fiscal address:", e);
  }

  return {
    id: dbClient.id,
    name: dbClient.name || "",
    company: dbClient.name || "", // Using name as company for now
    email: dbClient.email || "",
    phone: dbClient.phone_number || "",
    address: "",
    creditScore: Math.random() * 10, // Random credit score for visualization
    riskScore: Math.random() * 10,
    paymentHistory: ["good", "average", "poor"][
      Math.floor(Math.random() * 3)
    ] as "good" | "average" | "poor",
    isActive: dbClient.is_active ?? true,
    is_active: dbClient.is_active ?? true,
    deactivation_reason: dbClient.deactivation_reason || null,
    sellerName: dbClient.seller_name || "No asignado",
    sellerId: dbClient.assigned_seller || "", // Keep sellerId for backward compatibility
    assigned_seller: dbClient.assigned_seller || "", // Primary field for seller ID (matches DB column)
    taxId: dbClient.tax_id || "",
    insuredAmount: dbClient.insured_amount || 0,
    trailingPaymentDays: dbClient.trailing_payment_days || 0,
    risk_checked: dbClient.risk_checked || false,
    salesConditionId: dbClient.sales_condition_id || "",
    salesConditionName: salesConditionName,
    contact_person: dbClient.contact_person || "",
    fiscalStreet: fiscalStreet,
    fiscalComuna: fiscalComuna,
    fiscalRegion: fiscalRegion,
    internal_notes: dbClient.internal_notes || "",
    deleted_at: dbClient.deleted_at || null,
    collection_contact_person: dbClient.collection_contact_person || "",
    collection_contact_email: dbClient.collection_contact_email || "",
    collection_contact_phone: dbClient.collection_contact_phone || "",
    sales_channel: dbClient.sales_channel || "",
    auxiliary_type: dbClient.auxiliary_type || "",
    sales_channel_name: dbClient.sales_channel_name || "",
    auxiliary_type_name: dbClient.auxiliary_type_name || "",
  };
};

const ClientsPage = () => {
  // Access user profile information
  const { id: userId, role: userRole } = useProfileStore();
  const isSeller = userRole === "seller";
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperAdmin = userRole === "superadmin";

  const [searchQuery, setSearchQuery] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active"); // active, inactive, all
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isSalesChannelModalOpen, setIsSalesChannelModalOpen] = useState(false);
  const [isAuxiliaryTypeModalOpen, setIsAuxiliaryTypeModalOpen] =
    useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingAddresses, setIsExportingAddresses] = useState(false);
  const [isAddressImportDialogOpen, setIsAddressImportDialogOpen] =
    useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { importClients } = useClientImport();

  // Use the optimized clients hook
  const {
    clients: optimizedClients,
    isLoading,
    error,
    totalCount,
    refetch: refetchClients,
  } = useOptimizedClients({
    sellerFilter,
    searchQuery,
    paymentHistoryFilter: filterValue,
    activeFilter: activeTab,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  });

  // Transform optimized clients to match your existing interface
  const clients = optimizedClients.map(transformOptimizedClient);
  const filteredClients = clients; // No need for additional filtering as it's handled by the database function

  // Handle errors from the optimized hook
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los clientes.",
        variant: "destructive",
      });
    }
  }, [error]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterValue, sellerFilter, activeTab]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;

  const { sortField, sortDirection, handleSort, sortedClients } =
    useClientSort(filteredClients);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
  };

  const handleSellerFilterChange = (value: string) => {
    console.log("Seller filter changed to:", value);
    setSellerFilter(value);
    // The fetchClients will be triggered by the useEffect
  };

  const handleClientAdded = (newClient: ClientData) => {
    // Refresh the clients list after adding a new client
    refetchClients();

    toast({
      title: "Cliente Añadido",
      description: "El nuevo cliente ha sido añadido con éxito.",
    });
  };

  const handleClientImport = async (clientsData: Partial<Client>[]) => {
    await importClients(clientsData);
    // Refresh the clients list after import
    await refetchClients();
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      // Fetch ALL clients for export (not just the current page)
      // For now, use direct query while we debug the function signature
      let exportQuery = supabase
        .from("clients")
        .select(
          `
          *,
          profiles:assigned_seller(name, lastname, email),
          sales_conditions:sales_condition_id(name, type),
          addresses:fiscal_address_id(street, number, city, comuna, state),
          sales_channels:sales_channel(id, name),
          auxiliary_types:auxiliary_type(id, name)
        `
        )
        .is("deleted_at", null);

      // Apply seller filter
      if (userRole === "seller") {
        exportQuery = exportQuery.eq("assigned_seller", userId);
      } else if (sellerFilter !== "all") {
        exportQuery = exportQuery.eq("assigned_seller", sellerFilter);
      }

      // Apply active/inactive filter
      if (activeTab === "active") {
        exportQuery = exportQuery.eq("is_active", true);
      } else if (activeTab === "inactive") {
        exportQuery = exportQuery.eq("is_active", false);
      }

      // Apply search query
      if (searchQuery) {
        exportQuery = exportQuery.or(
          `name.ilike.%${searchQuery}%,tax_id.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,phone_number.ilike.%${searchQuery}%`
        );
      }

      const { data: allClientsData, error } = await exportQuery;

      if (error) {
        throw error;
      }

      // Fetch seller history for all clients to get previous seller information
      const clientIds = allClientsData?.map((client) => client.id) || [];

      // Debug: Let's see what we have
      console.log(
        "🔍 Fetching seller history for",
        clientIds.length,
        "clients"
      );

      // Batch the client IDs to avoid URL length limits
      const batchSize = 100; // Process 100 client IDs at a time
      const batchPromises = [];

      // Build array of promises (parallel execution)
      for (let i = 0; i < clientIds.length; i += batchSize) {
        const batch = clientIds.slice(i, i + batchSize);
        batchPromises.push(
          supabase
            .from("client_seller_history")
            .select(
              `
              client_id,
              seller_id,
              assigned_at,
              unassigned_at,
              profiles:seller_id(name, lastname)
            `
            )
            .in("client_id", batch)
            .order("assigned_at", { ascending: false })
        );
      }

      console.log(`📦 Fetching ${batchPromises.length} batches in parallel...`);

      // Execute all batches in parallel
      const batchResults = await Promise.all(batchPromises);

      // Combine all batches, filtering out errors
      const sellerHistoryData = batchResults
        .filter((result) => {
          if (result.error) {
            console.error("Error fetching seller history batch:", result.error);
            return false;
          }
          return true;
        })
        .flatMap((result) => result.data || []);

      console.log(
        "📊 Seller history data:",
        sellerHistoryData?.length,
        "records"
      );
      // Debug first few records
      if (sellerHistoryData && sellerHistoryData.length > 0) {
        console.log("Sample records:", sellerHistoryData.slice(0, 3));
      }

      // Create a map of client ID to their previous seller info
      const previousSellerMap = new Map();

      // Group seller history by client_id
      const historyByClient = new Map<string, any[]>();
      sellerHistoryData?.forEach((history) => {
        if (!historyByClient.has(history.client_id)) {
          historyByClient.set(history.client_id, []);
        }
        historyByClient.get(history.client_id)?.push(history);
      });

      // Process each client's history to find previous seller
      historyByClient.forEach((clientHistory, clientId) => {
        // Sort by assigned_at descending to get most recent first
        clientHistory.sort(
          (a, b) =>
            new Date(b.assigned_at).getTime() -
            new Date(a.assigned_at).getTime()
        );

        console.log(
          `📋 Client ${clientId} has ${clientHistory.length} history records:`,
          clientHistory.map((h) => ({
            seller: h.profiles
              ? `${h.profiles.name} ${h.profiles.lastname}`
              : "Unknown",
            assigned: h.assigned_at,
            unassigned: h.unassigned_at,
          }))
        );

        if (clientHistory.length > 1) {
          // Get the second most recent assignment (previous seller)
          const previousAssignment = clientHistory[1];
          const sellerName = previousAssignment.profiles
            ? `${previousAssignment.profiles.name || ""} ${previousAssignment.profiles.lastname || ""}`.trim()
            : "Vendedor no encontrado";

          console.log(`✅ Client ${clientId} - Previous seller: ${sellerName}`);

          previousSellerMap.set(clientId, {
            previousSeller: sellerName,
            lastUpdateDate:
              previousAssignment.unassigned_at ||
              previousAssignment.assigned_at,
          });
        } else if (clientHistory.length === 1) {
          // Only one assignment, check if there was an unassignment (indicating a previous seller existed)
          const currentAssignment = clientHistory[0];
          if (currentAssignment.unassigned_at) {
            // This seller was unassigned, so they are actually a previous seller
            const sellerName = currentAssignment.profiles
              ? `${currentAssignment.profiles.name || ""} ${currentAssignment.profiles.lastname || ""}`.trim()
              : "Vendedor no encontrado";

            console.log(
              `✅ Client ${clientId} - Unassigned seller as previous: ${sellerName}`
            );

            previousSellerMap.set(clientId, {
              previousSeller: sellerName,
              lastUpdateDate: currentAssignment.unassigned_at,
            });
          } else {
            // Only current assignment, no previous seller
            console.log(
              `ℹ️ Client ${clientId} - No previous seller (only current assignment)`
            );
            previousSellerMap.set(clientId, {
              previousSeller: "Sin vendedor anterior",
              lastUpdateDate: null,
            });
          }
        } else {
          // No history at all
          console.log(`ℹ️ Client ${clientId} - No seller history at all`);
          previousSellerMap.set(clientId, {
            previousSeller: "Sin vendedor anterior",
            lastUpdateDate: null,
          });
        }
      });

      // Transform the data to match the existing client interface
      const allClients = (allClientsData || []).map((client: any) => {
        const previousSellerInfo = previousSellerMap.get(client.id) || {
          previousSeller: "Sin vendedor anterior",
          lastUpdateDate: null,
        };

        return {
          id: client.id,
          name: client.name || "",
          company: client.name || "",
          email: client.email || "",
          phone: client.phone_number || "",
          address: "",
          creditScore: Math.random() * 10,
          riskScore: Math.random() * 10,
          paymentHistory: ["good", "average", "poor"][
            Math.floor(Math.random() * 3)
          ] as "good" | "average" | "poor",
          isActive: client.is_active ?? true,
          is_active: client.is_active ?? true,
          deactivation_reason: client.deactivation_reason || null,
          sellerName: client.profiles
            ? `${client.profiles.name || ""} ${client.profiles.lastname || ""}`.trim()
            : "No asignado",
          sellerId: client.assigned_seller || "",
          assigned_seller: client.assigned_seller || "",
          taxId: client.tax_id || "",
          insuredAmount: client.insured_amount || 0,
          trailingPaymentDays: client.trailing_payment_days || 0,
          risk_checked: client.risk_checked || false,
          salesConditionId: client.sales_condition_id || "",
          salesConditionName:
            client.sales_conditions?.name || "No especificada",
          contact_person: client.contact_person || "",
          fiscalStreet: client.addresses?.street || "",
          fiscalComuna: client.addresses?.comuna || "",
          fiscalRegion: client.addresses?.state || "",
          internal_notes: client.internal_notes || "",
          deleted_at: client.deleted_at || null,
          // Add previous seller information
          previousSeller: previousSellerInfo.previousSeller,
          lastUpdateDate: previousSellerInfo.lastUpdateDate,
          // Add new contact fields
          collection_contact_person: client.collection_contact_person || "",
          collection_contact_email: client.collection_contact_email || "",
          collection_contact_phone: client.collection_contact_phone || "",
          sales_channel: client.sales_channel || "",
          auxiliary_type: client.auxiliary_type || "",
          sales_channel_name: (client as any).sales_channels?.name || "",
          auxiliary_type_name: (client as any).auxiliary_types?.name || "",
        };
      });

      // Prepare data for Excel export
      const excelData = allClients.map((client) => ({
        Nombre: client.company,
        "RUT/Tax ID": client.taxId,
        "Contacto Comercial": client.contact_person || client.name,
        "Mail Contacto Comercial": client.email,
        "Teléfono Contacto Comercial": client.phone,
        "Contacto Cobranza": client.collection_contact_person || "",
        "Mail Contacto Cobranza": client.collection_contact_email || "",
        "Teléfono Contacto Cobranza": client.collection_contact_phone || "",
        "Vendedor Asignado": client.sellerName,
        "Vendedor Anterior": client.previousSeller,
        "Monto Asegurado": client.insuredAmount,
        "Días de Pago": client.trailingPaymentDays,
        "Condición de Venta": client.salesConditionName || "No especificada",
        Calle: client.fiscalStreet || "N/A",
        Comuna: client.fiscalComuna || "N/A",
        Región: client.fiscalRegion || "N/A",
        "Canal de Venta": client.sales_channel_name || "",
        "Tipo de Auxiliar": client.auxiliary_type_name || "",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 25 }, // Nombre
        { wch: 15 }, // RUT/Tax ID
        { wch: 25 }, // Contacto Comercial
        { wch: 25 }, // Mail Contacto Comercial
        { wch: 20 }, // Teléfono Contacto Comercial
        { wch: 25 }, // Contacto Cobranza
        { wch: 25 }, // Mail Contacto Cobranza
        { wch: 20 }, // Teléfono Contacto Cobranza
        { wch: 20 }, // Vendedor Asignado
        { wch: 20 }, // Vendedor Anterior
        { wch: 15 }, // Monto Asegurado
        { wch: 12 }, // Días de Pago
        { wch: 20 }, // Condición de Venta
        { wch: 30 }, // Calle
        { wch: 20 }, // Comuna
        { wch: 25 }, // Región
        { wch: 20 }, // Canal de Venta
        { wch: 20 }, // Tipo de Auxiliar
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Clientes");

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `clientes_${currentDate}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Excel Exportado",
        description: `Se ha descargado el archivo ${filename} con ${allClients.length} clientes, incluyendo información del vendedor anterior.`,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAddresses = async () => {
    setIsExportingAddresses(true);
    try {
      await exportAddressesToExcel();
      toast({
        title: "Direcciones Exportadas",
        description: "Se ha descargado el archivo con todas las direcciones.",
      });
    } catch (error) {
      console.error("Error exporting addresses:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar las direcciones.",
        variant: "destructive",
      });
    } finally {
      setIsExportingAddresses(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus("Leyendo archivo...");

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          setImportStatus("Procesando Excel...");

          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });

          // Look for the "Clientes" sheet first, then fallback to first sheet
          const sheetName =
            workbook.SheetNames.find((name) => name === "Clientes") ||
            workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            throw new Error(
              "El archivo Excel debe contener al menos un encabezado y una línea de datos"
            );
          }

          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1) as any[][];

          // Field mapping for client data
          const fieldMap: Record<string, string> = {
            ID: "id", // Keep for backward compatibility
            Nombre: "name",
            "RUT/Tax ID": "taxId",
            "Tax ID": "taxId",
            RUT: "taxId",
            "Contacto Comercial": "contact_person",
            "Persona de Contacto": "contact_person", // Keep for backward compatibility
            "Mail Contacto Comercial": "email",
            Email: "email", // Keep for backward compatibility
            "Teléfono Contacto Comercial": "phone",
            Teléfono: "phone", // Keep for backward compatibility
            Telefono: "phone",
            "Vendedor Asignado": "sellerName", // Map seller name for reference
            "ID Vendedor Asignado": "sellerId", // Keep this for backward compatibility
            "Monto Asegurado": "insuredAmount",
            "Días de Pago": "trailingPaymentDays",
            "Condición de Venta": "salesConditionName", // Map condition name for reference
            "ID Condición de Venta": "salesConditionId",
            "Contacto Cobranza": "collection_contact_person",
            "Mail Contacto Cobranza": "collection_contact_email",
            "Teléfono Contacto Cobranza": "collection_contact_phone",
            "Canal de Venta": "sales_channel_name", // Map to name field (ignored in comparison, export uses names not UUIDs)
            "Tipo de Auxiliar": "auxiliary_type_name", // Map to name field (ignored in comparison, export uses names not UUIDs)
          };

          setImportStatus("Cargando vendedores...");

          // Fetch all sellers to map names to IDs
          const { data: sellers } = await supabase
            .from("profiles")
            .select("id, name, lastname")
            .eq("role", "seller");

          // Create a map from seller names to IDs
          const sellerNameToIdMap = new Map<string, string>();
          sellers?.forEach((seller) => {
            const fullName =
              `${seller.name || ""} ${seller.lastname || ""}`.trim();
            if (fullName) {
              sellerNameToIdMap.set(fullName, seller.id);
            }
          });

          // Fetch auxiliary types to map names to IDs
          const { data: auxiliaryTypes } = await supabase
            .from("auxiliary_types")
            .select("id, name");

          const auxiliaryTypeNameToIdMap = new Map<string, string>();
          auxiliaryTypes?.forEach((type) => {
            if (type.name) {
              auxiliaryTypeNameToIdMap.set(type.name, type.id);
            }
          });

          // Fetch sales channels to map names to IDs
          const { data: salesChannels } = await supabase
            .from("sales_channels")
            .select("id, name");

          const salesChannelNameToIdMap = new Map<string, string>();
          salesChannels?.forEach((channel) => {
            if (channel.name) {
              salesChannelNameToIdMap.set(channel.name, channel.id);
            }
          });

          setImportStatus("Procesando datos de clientes...");

          const clients: Partial<Client>[] = [];

          for (const row of rows) {
            if (!row || row.length === 0) continue;

            const client: any = {};

            headers.forEach((header, index) => {
              const field = fieldMap[header];
              if (field && row[index] !== undefined && row[index] !== null) {
                const value = String(row[index]).trim();

                if (value === "" || value === "Activo" || value === "Eliminado")
                  return;

                // Handle boolean fields
                if (field === "risk_checked") {
                  client[field] =
                    value.toLowerCase() === "sí" ||
                    value.toLowerCase() === "si" ||
                    value === "true";
                  return;
                }

                // Convert numeric values to numbers
                if (
                  field === "insuredAmount" ||
                  field === "trailingPaymentDays"
                ) {
                  const numValue = value
                    .replace(/[^\d.,]/g, "")
                    .replace(",", ".");
                  client[field] = numValue ? parseFloat(numValue) : 0;
                } else {
                  // String values
                  client[field] = value;
                }
              }
            });

            // Convert seller name to ID if we have a seller name but no seller ID
            if (client.sellerName && !client.sellerId) {
              const sellerId = sellerNameToIdMap.get(client.sellerName);
              if (sellerId) {
                client.sellerId = sellerId;
              }
            }

            // Convert auxiliary type name to ID
            if (client.auxiliary_type_name && !client.auxiliary_type) {
              const auxiliaryTypeId = auxiliaryTypeNameToIdMap.get(
                client.auxiliary_type_name
              );
              if (auxiliaryTypeId) {
                client.auxiliary_type = auxiliaryTypeId;
              }
            }

            // Convert sales channel name to ID
            if (client.sales_channel_name && !client.sales_channel) {
              const salesChannelId = salesChannelNameToIdMap.get(
                client.sales_channel_name
              );
              if (salesChannelId) {
                client.sales_channel = salesChannelId;
              }
            }

            // Ensure we have either an ID (for updates) or required fields for new clients
            if (client.id || client.name || client.taxId || client.email) {
              clients.push(client);
            }
          }

          if (clients.length === 0) {
            toast({
              title: "Error de importación",
              description: "No se pudieron extraer clientes del archivo Excel.",
              variant: "destructive",
            });
            return;
          }

          // ALWAYS USE UPDATE MODE - NO NEW CLIENT CREATION ALLOWED
          const detectedUpdateMode = true;

          // Note: Pre-validation query removed to avoid URL length issues with 8000+ clients
          // The useClientImport hook handles matching internally by fetching ALL clients
          console.log(`🔍 Import validation:`, {
            totalClients: clients.length,
            mode: "UPDATE_ONLY",
          });

          setImportStatus("Actualizando clientes existentes...");

          await handleClientImport(clients);

          setImportStatus("¡Importación completada!");

          // Clear the file input
          if (event.target) {
            event.target.value = "";
          }

          // Reset states after a brief delay
          setTimeout(() => {
            setIsImporting(false);
            setImportStatus("");
          }, 1000);
        } catch (error) {
          console.error("Excel import error:", error);
          toast({
            title: "Error de importación",
            description:
              error instanceof Error
                ? error.message
                : "Error al importar desde Excel.",
            variant: "destructive",
          });
        } finally {
          setIsImporting(false);
          setImportStatus("");
        }
      };
      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Error al leer el archivo Excel.",
          variant: "destructive",
        });
        setIsImporting(false);
        setImportStatus("");
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error("File upload error:", error);
      toast({
        title: "Error",
        description: "Error al procesar el archivo.",
        variant: "destructive",
      });
      setIsImporting(false);
      setImportStatus("");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Clientes"
        description="Gestión de clientes activos e inactivos"
        actions={
          <AddClientDialog
            isOpen={isAddClientOpen}
            setOpen={setIsAddClientOpen}
            onClientAdded={handleClientAdded}
          />
        }
      />

      {/* Hidden file input for client import (triggered from the actions menu) */}
      <input
        id="client-import"
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileUpload}
        disabled={isImporting}
        className="hidden"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <Card className="rounded shadow-sm">
          <CardHeader className="bg-white">
            <div className="flex flex-col gap-3">
              {/* Toolbar: tabs left; search, filters and actions right */}
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <TabsList className="grid h-9 w-full grid-cols-2 sm:inline-flex sm:w-auto">
                  <TabsTrigger value="active" className="py-1 duration-300">
                    Clientes Activos
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="py-1 duration-300">
                    Clientes Inactivos
                  </TabsTrigger>
                </TabsList>

                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  <ClientSearchBar
                    searchQuery={searchQuery}
                    setSearchQuery={handleSearch}
                  />

                  {/* Seller filter and actions menu are admin-only */}
                  {isAdmin && (
                    <>
                      <SellerFilter
                        value={sellerFilter}
                        onChange={handleSellerFilterChange}
                      />

                      <Separator
                        orientation="vertical"
                        className="hidden h-6 sm:block"
                      />

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-full shrink-0 sm:w-auto"
                          >
                            <Settings2 className="mr-2 h-4 w-4" />
                            Acciones
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Clientes</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isImporting}
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            {isImporting
                              ? "Importando..."
                              : "Importar clientes"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={exportToExcel}
                            disabled={isExporting || isLoading}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {isExporting ? "Exportando..." : "Exportar Excel"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>Direcciones</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => setIsAddressImportDialogOpen(true)}
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            Importar direcciones
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={handleExportAddresses}
                            disabled={isExportingAddresses}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {isExportingAddresses
                              ? "Exportando..."
                              : "Exportar direcciones"}
                          </DropdownMenuItem>
                          {isSuperAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>
                                Configuración
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => setIsSalesChannelModalOpen(true)}
                              >
                                <Settings2 className="mr-2 h-4 w-4" />
                                Gestionar canales
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setIsAuxiliaryTypeModalOpen(true)
                                }
                              >
                                <Settings2 className="mr-2 h-4 w-4" />
                                Gestionar tipos
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              </div>

              {/* Loading bar for import */}
              {isImporting && (
                <div className="space-y-2">
                  <div className="flex items-center justify-center text-sm">
                    <span className="text-muted-foreground">
                      {importStatus}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div className="h-full w-full animate-pulse rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <TabsContent
              value="active"
              className="mt-0 duration-300 ease-out animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none"
            >
              {isLoading ? (
                <div className="space-y-4">
                  {/* Desktop skeleton table */}
                  <div className="hidden overflow-x-auto border-b md:block">
                    <div className="w-full">
                      {/* Table header skeleton */}
                      <div className="border-b bg-gray-50 p-4">
                        <div className="grid grid-cols-9 gap-4">
                          <div className="w-[180px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-8 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[180px]">
                            <div className="h-4 w-12 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[150px]">
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[130px]">
                            <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[130px]">
                            <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[140px]">
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                      </div>
                      {/* Table rows skeleton */}
                      <div className="divide-y">
                        {Array.from({ length: 8 }).map((_, index) => (
                          <div key={index} className="p-4">
                            <div className="grid grid-cols-9 gap-4">
                              <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-40 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-28 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200"></div>
                              <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Mobile skeleton cards */}
                  <div className="space-y-4 p-4 md:hidden">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="rounded-lg border bg-white p-4 shadow-sm"
                      >
                        <div className="mb-3 space-y-2">
                          <div className="h-5 w-48 animate-pulse rounded bg-gray-200"></div>
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                        </div>
                        <div className="mb-3 space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-40 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <div className="space-y-1">
                            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="space-y-1">
                            <div className="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-12 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination skeleton */}
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                      <div className="flex space-x-2">
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                      </div>
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                    </div>
                  </div>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-8 text-center">
                  {isSeller
                    ? "No tienes clientes asignados. Contacta a un administrador para que te asigne clientes."
                    : "No se encontraron clientes con los filtros seleccionados."}
                </div>
              ) : (
                <div>
                  <div className="overflow-x-auto border-b">
                    <ClientsTable
                      clients={sortedClients}
                      onSort={handleSort}
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </div>
                  <div className="px-4">
                    <ClientsPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                      startIndex={startIndex}
                      pageSize={pageSize}
                      totalClients={totalCount}
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="inactive"
              className="mt-0 duration-300 ease-out animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none"
            >
              {isLoading ? (
                <div className="space-y-4">
                  {/* Desktop skeleton table */}
                  <div className="hidden overflow-x-auto border-b md:block">
                    <div className="w-full">
                      {/* Table header skeleton */}
                      <div className="border-b bg-gray-50 p-4">
                        <div className="grid grid-cols-9 gap-4">
                          <div className="w-[180px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-8 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[180px]">
                            <div className="h-4 w-12 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[150px]">
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[130px]">
                            <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[110px]">
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[130px]">
                            <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="w-[140px]">
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                      </div>
                      {/* Table rows skeleton */}
                      <div className="divide-y">
                        {Array.from({ length: 8 }).map((_, index) => (
                          <div key={index} className="p-4">
                            <div className="grid grid-cols-9 gap-4">
                              <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-40 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-28 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                              <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200"></div>
                              <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Mobile skeleton cards */}
                  <div className="space-y-4 p-4 md:hidden">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="rounded-lg border bg-white p-4 shadow-sm"
                      >
                        <div className="mb-3 space-y-2">
                          <div className="h-5 w-48 animate-pulse rounded bg-gray-200"></div>
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                        </div>
                        <div className="mb-3 space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-40 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <div className="space-y-1">
                            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
                          </div>
                          <div className="space-y-1">
                            <div className="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
                            <div className="h-4 w-12 animate-pulse rounded bg-gray-200"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination skeleton */}
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                      <div className="flex space-x-2">
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                        <div className="h-8 w-8 animate-pulse rounded bg-gray-200"></div>
                      </div>
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                    </div>
                  </div>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-8 text-center">
                  {isSeller
                    ? "No tienes clientes inactivos asignados."
                    : "No se encontraron clientes inactivos con los filtros seleccionados."}
                </div>
              ) : (
                <div>
                  <div className="overflow-x-auto border-b">
                    <ClientsTable
                      clients={sortedClients}
                      onSort={handleSort}
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </div>
                  <div className="px-4">
                    <ClientsPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                      startIndex={startIndex}
                      pageSize={pageSize}
                      totalClients={totalCount}
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {/* Management Modals */}
      {isSuperAdmin && (
        <>
          <SalesChannelManagementModal
            isOpen={isSalesChannelModalOpen}
            onOpenChange={setIsSalesChannelModalOpen}
          />
          <AuxiliaryTypeManagementModal
            isOpen={isAuxiliaryTypeModalOpen}
            onOpenChange={setIsAuxiliaryTypeModalOpen}
          />
        </>
      )}

      {/* Address Import Dialog */}
      {isAdmin && (
        <AddressImportDialog
          open={isAddressImportDialogOpen}
          onOpenChange={setIsAddressImportDialogOpen}
          onSuccess={() => refetchClients()}
        />
      )}
    </PageContainer>
  );
};

export default ClientsPage;
